import { useState, useEffect, useRef, useMemo } from "react";
import { FiSend, FiX } from "react-icons/fi";
import clsx from "clsx";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { CSSTransition } from "react-transition-group";
import Input from "components/form/input";
import ChatMessage from "./ChatMessage";
import {
  getHistory,
  sendMessage,
  resetChat,
  ChatMessage as ChatMessageType
} from "api/chat";
import * as subscriptionsApi from "api/subscriptions";
import { captureException } from "@sentry/nextjs";
import { track } from "utils/tracker";
import { useUser } from "hooks/user";
import { useNotifications } from "hooks/notifications";
import { useChatLimits } from "hooks/chat_limits";

interface ChatInterfaceProps {
  questionId: number;
  onClose: () => void;
  isMobile: boolean;
}

export default function ChatInterface({
  questionId,
  onClose,
  isMobile
}: ChatInterfaceProps): JSX.Element {
  const { user } = useUser();
  const { setNotification } = useNotifications();
  const { allLimits, userLimits, userPlan } = useChatLimits();

  const welcomeMessage = useMemo<ChatMessageType>(
    () => ({
      message:
        "Olá! Sou uma <b>inteligência artificial</b> e posso cometer erros, mas estou aqui para te ajudar a se preparar para o vestibular!<br><br>A <b>nossa conversa será salva</b> e pode ser analisada por humanos para melhorar a qualidade das minhas respostas.<br><br>Ficou com alguma dúvida sobre a questão?",
      role: "assistant"
    }),
    []
  );

  const conversationStarters = useMemo(
    () => [
      "Não entendi a explicação da resposta",
      "Por que as outras alternativas estão erradas?"
    ],
    []
  );

  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");
  const [chatInputDisabled, setChatInputDisabled] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const receivedMessageResponseRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const MAX_CHARS = 500;
  const queryClient = useQueryClient();

  const sendMessageMutation = useMutation(
    (message: string) => sendMessage(questionId, message),
    {
      onMutate: (message) => {
        const userMessage: ChatMessageType = {
          message: message,
          role: "user"
        };
        setMessages((prev) => [...prev, userMessage]);
        setInputValue("");
        setShowSuggestions(false);
      },
      onSuccess: (response) => {
        receivedMessageResponseRef.current = true;

        const assistantMessage: ChatMessageType = {
          message: response.body.response,
          role: "assistant"
        };
        setMessages((prev) => [...prev, assistantMessage]);

        queryClient.invalidateQueries(["chatHistory", questionId]);
      },
      onError: (error: any) => {
        if (!user) return handleNotSignedInError();

        if (error.statusCode === 422) {
          return handleUnprocessableEntityError(error);
        }

        setError("Falha ao enviar mensagem. Tente novamente mais tarde.");
        captureException(error);
      }
    }
  );

  const resetChatMutation = useMutation(() => resetChat(questionId), {
    onSuccess: () => {
      queryClient.invalidateQueries(["chatHistory", questionId]);
      setMessages([welcomeMessage]);
      setChatInputDisabled(false);
      setShowSuggestions(true);
      setNotification({
        title: "Conversa reiniciada",
        text: "Sua conversa foi reiniciada com sucesso.",
        type: "success"
      });
      track("Reset chat conversation", { questionId });
    },
    onError: (error) => {
      setError(
        "Não foi possível resetar a conversa. Tente novamente mais tarde."
      );
      captureException(error);
    }
  });

  const inputDisabled =
    sendMessageMutation.isLoading ||
    resetChatMutation.isLoading ||
    !inputValue.trim() ||
    chatInputDisabled;

  const { data: historyData, isLoading: isLoadingHistory } = useQuery(
    ["chatHistory", questionId],
    () => getHistory(questionId),
    {
      refetchOnWindowFocus: false,
      staleTime: 60 * 10000, // 10 minutes
      retry: 1,
      onSuccess: (historyData) => {
        const historyMessages = historyData?.body?.history || [];
        setMessages([welcomeMessage, ...historyMessages]);
        setShowSuggestions(historyMessages.length === 0);
      }
    }
  );

  const historyMessages = historyData?.body?.history || [];
  const [messages, setMessages] = useState<ChatMessageType[]>([
    welcomeMessage,
    ...historyMessages
  ]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    setTimeout(() => {
      if (!receivedMessageResponseRef.current) {
        messagesEndRef.current?.scrollIntoView();
        return;
      }

      const messagesContainer = messagesContainerRef.current;
      const messageElements =
        messagesContainer.querySelectorAll(".chat-message");

      if (messageElements.length > 0) {
        const lastMessage = messageElements[messageElements.length - 1];
        lastMessage.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 30);
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || sendMessageMutation.isLoading) return;
    submitMessage(inputValue.trim(), "custom");
  };

  const handleSuggestedMessageClick = (message: string) => {
    submitMessage(message, "suggestion");
  };

  const submitMessage = (message: string, source: "custom" | "suggestion") => {
    setError("");

    if (!user) {
      handleNotSignedInError();
      return;
    }

    track("Sent message on question chat", {
      questionId,
      message,
      source
    });

    sendMessageMutation.mutate(message);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNotSignedInError = () => {
    const encodedRedirectUrl = encodeURIComponent(window.location.href);

    const loginMessage: ChatMessageType = {
      message: `Para utilizar o chat, você precisa estar logado. Crie uma conta gratuita para começar a tirar suas dúvidas!<br><a class='btn btn-primary w-full block mt-4' href='/cadastro?redirect_to=${encodedRedirectUrl}'>Criar conta</a><a class='w-full block mt-4' href='/login?redirect_to=${encodedRedirectUrl}'>Fazer login</a>`,
      role: "assistant"
    };

    setMessages((prev) => [...prev, loginMessage]);
    setInputValue("");
    setChatInputDisabled(true);
    setShowSuggestions(false);
    track("Not signed in message on question chat", { questionId });
  };

  const handleConversationLimitError = () => {
    const limitMessage: ChatMessageType = {
      message: `Ops, infelizmente chegamos no limite de mensagens dessa conversa. Esse limite existe para garantir a qualidade das respostas.<br><br>Você pode recomeçar a conversa para continuar tirando suas dúvidas. Essa ação irá apagar todas as mensagens anteriores dessa questão.<br><a class='btn btn-secondary w-full block mt-4' href="#" id="reset-chat-button" onClick="event.preventDefault()">Recomeçar chat</a>`,
      role: "assistant"
    };

    setMessages((prev) => [...prev, limitMessage]);
    setInputValue("");
    setChatInputDisabled(true);
    setShowSuggestions(false);

    // Add event listener to the reset button after the DOM is updated
    setTimeout(() => {
      const resetButton = document.getElementById("reset-chat-button");
      if (resetButton) {
        resetButton.onclick = (e) => {
          e.preventDefault();
          resetChatMutation.mutate();
        };
      }
    }, 100);

    track("Reached conversation limit on question chat", { questionId });
  };

  const handleDailyLimitError = () => {
    const isPaidUser = userPlan === "paid";

    const baseMessage = `Opa! Parece que você atingiu o <a href="/limites/chat">limite diário</a> de ${userLimits.questionChatMessages} mensagens`;
    const subscriptionMessage: ChatMessageType = {
      message: isPaidUser
        ? `${baseMessage}, mas não se preocupe, amanhã ele será renovado!`
        : `${baseMessage}.<br><br>Aumente seu limite para ${
            allLimits.paid.questionChatMessages
          } mensagens diárias assinando agora por apenas R$19,90 por mês.<br><a class='btn btn-primary w-full block mt-4' href='/assinatura?redirectTo=${encodeURIComponent(
            window.location.href
          )}'>Assinar</a>`,
      role: "assistant"
    };

    setMessages((prev) => [...prev, subscriptionMessage]);
    setInputValue("");
    setChatInputDisabled(true);
    setShowSuggestions(false);
    reportQuestionLimitReached();
  };

  const handleUnprocessableEntityError = (error: any) => {
    const errorCodes = error.responseBody?.errors?.base || [];

    for (const errorItem of errorCodes) {
      const errorCode = errorItem.code;

      if (errorCode === "daily_message_limit_exceeded") {
        return handleDailyLimitError();
      }

      if (errorCode === "chat_message_limit_exceeded") {
        return handleConversationLimitError();
      }
    }

    // Fallback for other 422 errors
    const errorMessage = error.responseBody?.errors?.base
      .map((item) => item.message)
      .join("\n");

    const subscriptionMessage: ChatMessageType = {
      message: errorMessage,
      role: "assistant"
    };

    setMessages((prev) => [...prev, subscriptionMessage]);
    setInputValue("");
    track("Received error on question chat", {
      questionId,
      error: error.responseBody
    });
  };

  const reportQuestionLimitReached = () => {
    subscriptionsApi
      .get()
      .then((_response) => {
        track("Reached message limit on question chat", {
          questionId,
          subscribed: true
        });
      })
      .catch((error) => {
        if (error.statusCode !== 404) return captureException(error);

        track("Reached message limit on question chat", {
          questionId,
          subscribed: false
        });
      });
  };

  return (
    <div
      className={clsx(
        "flex flex-col bg-white",
        isMobile
          ? "fixed inset-0 z-50"
          : "w-[400px] border-l border-gray-200 fixed top-0 right-0 z-30 h-[100dvh]"
      )}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="m-0 text-lg font-semibold">Conversa</h3>
        <button
          onClick={onClose}
          className="hover:text-gray-700 bg-[transparent] mb-0 text-gray-500 border-none"
          data-title="Fechar chat"
        >
          <FiX size={24} />
        </button>
      </div>

      <div
        className="relative flex-1 p-4 overflow-y-auto"
        ref={messagesContainerRef}
      >
        <CSSTransition
          in={isLoadingHistory || resetChatMutation.isLoading}
          timeout={300}
          classNames="fade"
          unmountOnExit
          nodeRef={loadingRef}
        >
          <div
            ref={loadingRef}
            className="w-[398px] top-[80px] right-[1px] md:h-[calc(100dvh-80px)] fixed z-50 bottom-0 flex items-center justify-center h-auto bg-white md:bottom-0"
          >
            <div className="animate-pulse">Carregando...</div>
          </div>
        </CSSTransition>

        {messages.map((msg, index) => (
          <ChatMessage key={index} message={msg} />
        ))}

        {(sendMessageMutation.isLoading || resetChatMutation.isLoading) && (
          <div className="flex items-center ml-4 mt-6 text-gray-500">
            <div className="mr-1 w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
            <div
              className="mr-1 w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: "0.2s" }}
            ></div>
            <div
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: "0.4s" }}
            ></div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 border-t border-gray-200">
        {showSuggestions && !chatInputDisabled && messages.length === 1 && (
          <div className="mb-4">
            <p className="mb-4 mt-0 text-right text-emphasis-high text-base">
              Sugestões
            </p>
            <div className="flex flex-col items-end">
              {conversationStarters.map((starter, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestedMessageClick(starter)}
                  className="block mb-2 p-3 text-left text-emphasis-high bg-secondary-50 border-none rounded-lg hover:opacity-100 opacity-80 transition-opacity"
                >
                  {starter}
                </button>
              ))}
            </div>
          </div>
        )}
        <span className="block mb-2 text-error-dark text-base">{error}</span>
        <div className="relative flex items-end">
          <div className="flex-1">
            <Input
              name="chatMessage"
              value={inputValue}
              type="textarea"
              onChange={setInputValue}
              disabled={
                sendMessageMutation.isLoading ||
                resetChatMutation.isLoading ||
                chatInputDisabled
              }
              className="mb-0"
              inputClassName="focus:outline-none focus:ring-1 focus:ring-secondary-300 !h-[50px] py-3 resize-none"
              options={{
                placeholder: "Digite sua mensagem...",
                rows: 3,
                autoFocus: !isMobile,
                onKeyPress: handleKeyPress,
                maxLength: MAX_CHARS
              }}
            />
          </div>
          <button
            id="chat-submit"
            onClick={handleSendMessage}
            disabled={inputDisabled}
            className={clsx(
              "pt-[14px] pr-[14px] mb-[7px] flex items-center justify-center ml-2 p-3 w-12 h-12 rounded-full",
              inputDisabled
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-secondary-600 border-secondary-600 text-white hover:bg-secondary-700"
            )}
          >
            <FiSend size={18} className="rotate-45" />
          </button>
        </div>
      </div>
    </div>
  );
}
